import { useState, useEffect } from 'react'
import { imageManager, ImageFile } from '../utils/imageManager'

export function useImages(category?: 'gallery' | 'classes' | 'kits') {
  const [images, setImages] = useState<ImageFile[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const updateImages = () => {
      const allImages = category 
        ? imageManager.getImagesByCategory(category)
        : imageManager.getAllImages()
      setImages(allImages)
      setLoading(false)
    }

    updateImages()
    const unsubscribe = imageManager.subscribe(updateImages)
    return unsubscribe
  }, [category])

  return { images, loading }
}

export function useImage(imageId: string | undefined) {
  const [image, setImage] = useState<ImageFile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!imageId) {
      setImage(null)
      setLoading(false)
      return
    }

    const updateImage = () => {
      const foundImage = imageManager.getImage(imageId)
      setImage(foundImage)
      setLoading(false)
    }

    updateImage()
    const unsubscribe = imageManager.subscribe(updateImage)
    return unsubscribe
  }, [imageId])

  return { image, loading }
}

// Helper function to get image URL with fallback
export function getImageUrl(imagePath?: string, fallback?: string): string {
  if (!imagePath) return fallback || '/images/placeholder.jpg'
  
  // In a real app, this might transform the path for CDN usage
  return imagePath
}

// Hook for image picker functionality
export function useImagePicker() {
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null)
  const [pickerCategory, setPickerCategory] = useState<'gallery' | 'classes' | 'kits' | 'all'>('all')

  const openPicker = (category: 'gallery' | 'classes' | 'kits' | 'all' = 'all') => {
    setPickerCategory(category)
    setIsPickerOpen(true)
  }

  const closePicker = () => {
    setIsPickerOpen(false)
    setSelectedImage(null)
  }

  const selectImage = (image: ImageFile) => {
    setSelectedImage(image)
  }

  return {
    isPickerOpen,
    selectedImage,
    pickerCategory,
    openPicker,
    closePicker,
    selectImage
  }
}